import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  Plus,
  Edit,
  Trash2,
  Copy,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { GLOBAL_PRESETS } from "../../routes/routes";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { signOutRequest } from "../../redux/auth/auth.actions";
import { toastify } from "../../configs/utils";

// Styled Components
const Container = styled.div`
  padding: 0;
  width: 100%;
  background: white;
  min-height: 100vh;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const Content = styled.div`
  background: #ededed;
  border-radius: 0;
  border: none;
  border-top: 0;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  padding: 2rem;
  box-shadow: none;
  width: 100%;
  min-height: 100vh;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1.125rem 1.5rem;
  background: transparent;
  border-radius: 12px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  flex-wrap: wrap;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 1rem;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  max-width: 400px;

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.5rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  background: transparent;
  outline: none;
  height: 2.5rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;

  &:focus {
    background-color: #ffffff;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }

  &:hover {
    background-color: #ffffff;
    border-color: #9ca3af;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
`;

const SearchIcon = styled(Search)`
  color: #9ca3af;
  position: absolute;
  left: 1rem;
  pointer-events: none;
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;

  ${SearchInput} {
    padding-left: 2.5rem;
    padding-right: 2.5rem;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: color 0.2s ease;
  border-radius: 4px;

  &:hover {
    color: #374151;
    background: #f3f4f6;
  }
`;

const Button = styled.button`
  padding: 0.375rem 0.875rem;
  height: 2.125rem;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  justify-content: center;
  white-space: nowrap;

  &.primary {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
    box-shadow: 0 1px 2px rgba(59, 130, 246, 0.2);

    &:hover:not(:disabled) {
      background: #2563eb;
      border-color: #2563eb;
    }

    &:active:not(:disabled) {
      background: #1d4ed8;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  margin-bottom: 2rem;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  background: transparent;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
`;

const TableScrollArea = styled.div`
  max-height: 70vh;
  width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: auto;

  /* Scrollable, scrollbar never shown */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.875rem;
  background: transparent;
  margin: 0;
`;

const TableHead = styled.thead`
  background: #ededed;
  position: sticky;
  top: 0;
  z-index: 25;
`;

const TableHeader = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #4b5563;
  border-bottom: 1px solid rgba(17, 24, 39, 0.16);
  white-space: nowrap;
  font-size: 14px;
  letter-spacing: 0.01em;
  background: #ededed;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  transition: background-color 0.15s ease;

  &:hover td {
    background-color: rgba(17, 24, 39, 0.03);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(17, 24, 39, 0.08);
  vertical-align: middle;
  color: #374151;
  font-size: 0.875rem;
  background: #f3f3f3;
`;

const ActionsCell = styled(TableCell)`
  display: flex;
  gap: 0.5rem;
  text-align: right;
  justify-content: flex-end;
  white-space: nowrap;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;

  &:active {
    transform: translateY(0.5px);
  }

  &.edit {
    color: #2563eb;
    background: #eff6ff;
    border-color: #bfdbfe;

    &:hover {
      background: #dbeafe;
      border-color: #93c5fd;
    }
  }

  &.duplicate {
    color: #059669;
    background: #ecfdf5;
    border-color: #a7f3d0;

    &:hover {
      background: #d1fae5;
      border-color: #6ee7b7;
    }
  }

  &.delete {
    color: #dc2626;
    background: #fef2f2;
    border-color: #fecaca;

    &:hover {
      background: #fee2e2;
      border-color: #fca5a5;
    }
  }
`;

const StatusMessage = styled.p`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
  font-size: 1.1rem;
`;

const ErrorMessage = styled.p`
  text-align: center;
  padding: 2rem;
  color: #ef4444;
  font-size: 1.1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin: 1rem 0;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.75rem;
`;

const PaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  background: transparent;
  color: #6b7280;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  svg {
    display: block;
    width: 15px;
    height: 15px;
  }

  &:hover:not(.disabled) {
    background: #f3f4f6;
    color: #111827;
  }

  &:active:not(.disabled) {
    transform: translateY(0.5px);
  }

  &.disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const PageNumber = styled.span`
  font-size: 0.8125rem;
  color: #374151;
  font-weight: 500;
  padding: 0 0.5rem;
`;

const GlobalPresetsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [presets, setPresets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const debounceTimerRef = useRef(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState(null);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // API Configuration
  const API_BASE_URL =
    import.meta.env.VITE_BASE_URL_DEVELOPMENT || "http://localhost:8000";
  const PRESETS_ENDPOINT = `${API_BASE_URL}/api/v1/cards/preset/`;

  const extractPageNumber = (url) => {
    if (!url) return null;
    try {
      const params = new URL(url).searchParams;
      return parseInt(params.get("page") || "1", 10);
    } catch {
      return null;
    }
  };

  const normalizePageUrl = (url) => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      return `${API_BASE_URL}${parsed.pathname}${parsed.search}`;
    } catch {
      return url;
    }
  };

  const buildBaseListUrl = () => {
    const params = new URLSearchParams();
    params.set("page_size", String(pageSize));
    if (debouncedSearchQuery && debouncedSearchQuery.trim().length > 0) {
      params.set("search", debouncedSearchQuery.trim());
    }
    return `${PRESETS_ENDPOINT}?${params.toString()}`;
  };

  const buildPageUrl = (page) => {
    const params = new URLSearchParams();
    params.set("page_size", String(pageSize));
    params.set("page", String(page));
    if (debouncedSearchQuery && debouncedSearchQuery.trim().length > 0) {
      params.set("search", debouncedSearchQuery.trim());
    }
    return `${PRESETS_ENDPOINT}?${params.toString()}`;
  };

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh");
    if (!refreshToken) {
      // Show toast message and logout when no refresh token is found
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("No refresh token found. Please login again.");
    }

    const refreshResponse = await fetch(
      `${API_BASE_URL}/api/v1/token/refresh/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      }
    );

    if (!refreshResponse.ok) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      // Show toast message and logout when refresh token fails
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("Failed to refresh token. Please login again.");
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  };

  const fetchPresets = useCallback(
    async (url = null, retry = false) => {
      try {
        setIsLoading(true);
        setError("");

        let token = localStorage.getItem("access");
        if (!token) token = await refreshAccessToken();

        const requestUrl = url ?? buildBaseListUrl();

        let response = await fetch(requestUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401 && !retry) {
          const newToken = await refreshAccessToken();
          response = await fetch(requestUrl, {
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          });
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch presets: ${response.status}`);
        }

        const data = await response.json();
        // Handle paginated response
        let presetsArray = [];
        if (Array.isArray(data)) {
          presetsArray = data;
          setCount(data.length);
          setNextPage(null);
          setPrevPage(null);
          setCurrentPage(1);
        } else if (data.results && Array.isArray(data.results)) {
          presetsArray = data.results;
          setCount(data.count || presetsArray.length);
          setNextPage(normalizePageUrl(data.next));
          setPrevPage(normalizePageUrl(data.previous));

          // Determine current page
          const pageFromNext = extractPageNumber(normalizePageUrl(data.next));
          const pageFromPrev = extractPageNumber(normalizePageUrl(data.previous));

          if (!pageFromNext && !pageFromPrev) {
            setCurrentPage(1);
          } else if (pageFromPrev) {
            setCurrentPage(pageFromPrev + 1);
          } else if (pageFromNext) {
            setCurrentPage(pageFromNext - 1);
          }
        } else if (data.id) {
          // Single preset object
          presetsArray = [data];
          setCount(1);
          setNextPage(null);
          setPrevPage(null);
          setCurrentPage(1);
        } else {
          setCount(0);
          setNextPage(null);
          setPrevPage(null);
          setCurrentPage(1);
        }
        setPresets(presetsArray);
      } catch (err) {
        setError(err?.message || "Something went wrong fetching presets");
        setPresets([]);
        setCount(0);
        setNextPage(null);
        setPrevPage(null);
        setCurrentPage(1);
      } finally {
        setIsLoading(false);
      }
    },
    [API_BASE_URL, PRESETS_ENDPOINT, pageSize, debouncedSearchQuery]
  );

  // Debounce search query
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce delay

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    // Reset to page 1 when debounced search query changes
    setCurrentPage(1);
    fetchPresets();
  }, [fetchPresets]);

  const handleAddNew = () => {
    navigate(`${GLOBAL_PRESETS}/new`);
  };

  const handleEdit = (preset) => {
    navigate(`${GLOBAL_PRESETS}/edit/${preset.id}`);
  };

  const handleDuplicate = async (preset) => {
    setIsLoading(true);
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      // First, fetch the full preset data
      let getResponse = await fetch(`${PRESETS_ENDPOINT}${preset.id}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (getResponse.status === 401) {
        const newToken = await refreshAccessToken();
        getResponse = await fetch(`${PRESETS_ENDPOINT}${preset.id}/`, {
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
        });
      }

      if (!getResponse.ok) {
        throw new Error(`Failed to fetch preset: ${getResponse.status}`);
      }

      const presetData = await getResponse.json();

      // Create duplicate with modified name
      const duplicatePayload = {
        name: `Copy of ${presetData.name || "Unnamed Preset"}`,
        preset_data: presetData.preset_data || {},
      };

      // Use POST API to create the duplicate
      let postResponse = await fetch(PRESETS_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(duplicatePayload),
      });

      if (postResponse.status === 401) {
        const newToken = await refreshAccessToken();
        postResponse = await fetch(PRESETS_ENDPOINT, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(duplicatePayload),
        });
      }

      if (postResponse.ok) {
        toast.success("Preset duplicated successfully!");
        // Maintain current page after duplication
        const urlToFetch = currentPage > 1 ? buildPageUrl(currentPage) : buildBaseListUrl();
        fetchPresets(urlToFetch);
      } else {
        const errorData = await postResponse.json();
        toast.error(
          `Failed to duplicate preset: ${errorData.detail || errorData.message || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("Error duplicating preset:", error);
      toast.error("Failed to duplicate preset");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (preset) => {
    setPresetToDelete(preset);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!presetToDelete) return;

    setIsLoading(true);
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      let response = await fetch(`${PRESETS_ENDPOINT}${presetToDelete.id}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(`${PRESETS_ENDPOINT}${presetToDelete.id}/`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${newToken}`,
          },
        });
      }

      if (response.ok || response.status === 204) {
        toast.success("Preset deleted successfully!");
        
        // Stay on the same page after deletion
        // If we deleted the last item on the current page and we're not on page 1, go to previous page
        // Otherwise, stay on the current page
        const itemsOnCurrentPage = presets.length;
        let pageToFetch = currentPage;
        
        // If we deleted the last item on the current page and there's a previous page, go to previous page
        if (itemsOnCurrentPage === 1 && currentPage > 1 && prevPage) {
          pageToFetch = currentPage - 1;
        }
        
        // Always use buildPageUrl to ensure we stay on the correct page
        // For page 1, we still need to include page=1 in the URL to maintain consistency
        const urlToFetch = buildPageUrl(pageToFetch);
        fetchPresets(urlToFetch);
      } else {
        const errorData = await response.json();
        toast.error(
          `Failed to delete preset: ${errorData.detail || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("Error deleting preset:", error);
      toast.error("Failed to delete preset");
    } finally {
      setIsLoading(false);
      setPresetToDelete(null);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  return (
    <Container>
      <Content>
        <Controls>
          <SearchContainer>
            <SearchWrapper>
              <SearchIcon size={16} />
              <SearchInput
                type="text"
                placeholder="Search presets by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <ClearButton onClick={handleClearSearch} title="Clear search">
                  <X size={16} />
                </ClearButton>
              )}
            </SearchWrapper>
          </SearchContainer>
          <Button onClick={handleAddNew} className="primary">
            <Plus size={16} />
            Add New Preset
          </Button>
        </Controls>

        {isLoading && <StatusMessage>Loading presets...</StatusMessage>}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {!isLoading && !error && presets.length === 0 && !debouncedSearchQuery.trim() && (
          <StatusMessage>
            No presets found. Click "Add New Preset" to create one.
          </StatusMessage>
        )}
        {!isLoading && !error && presets.length === 0 && debouncedSearchQuery.trim() && (
          <StatusMessage>
            No presets found matching "{debouncedSearchQuery}". Try a different search term.
          </StatusMessage>
        )}

        {!isLoading && !error && presets.length > 0 && (
          <>
            <TableContainer>
              <TableScrollArea>
                <Table>
                  <TableHead>
                    <tr>
                      <TableHeader>Name</TableHeader>
                      <TableHeader style={{ textAlign: "right",paddingRight: "3rem" }}>Actions</TableHeader>
                    </tr>
                  </TableHead>
                  <TableBody>
                    {presets.map((preset) => (
                      <TableRow key={preset.id}>
                        <TableCell>{preset.name || "Unnamed Preset"}</TableCell>
                        <ActionsCell>
                          <ActionButton
                            className="edit"
                            onClick={() => handleEdit(preset)}
                            title="Edit"
                          >
                            <Edit size={15} />
                          </ActionButton>
                          <ActionButton
                            className="duplicate"
                            onClick={() => handleDuplicate(preset)}
                            title="Duplicate"
                          >
                            <Copy size={15} />
                          </ActionButton>
                          <ActionButton
                            className="delete"
                            onClick={() => handleDeleteClick(preset)}
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </ActionButton>
                        </ActionsCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableScrollArea>
            </TableContainer>

            {/* Pagination controls */}
            {(nextPage || prevPage || count > pageSize) && (
              <PaginationContainer>
                <PaginationButton
                  onClick={() => prevPage && fetchPresets(prevPage)}
                  disabled={!prevPage}
                  className={!prevPage ? "disabled" : ""}
                  aria-label="Previous page"
                  title="Previous page"
                >
                  <ChevronLeft size={15} />
                </PaginationButton>

                <PageNumber>
                  Page {currentPage} of {Math.ceil(count / pageSize) || 1}
                </PageNumber>

                <PaginationButton
                  onClick={() => nextPage && fetchPresets(nextPage)}
                  disabled={!nextPage}
                  className={!nextPage ? "disabled" : ""}
                  aria-label="Next page"
                  title="Next page"
                >
                  <ChevronRight size={15} />
                </PaginationButton>
              </PaginationContainer>
            )}
          </>
        )}

        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setPresetToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Delete Preset"
          itemName={presetToDelete?.name || ""}
          message={`Are you sure you want to delete the preset "${presetToDelete?.name || ""}"? This action cannot be undone.`}
        />
      </Content>
    </Container>
  );
};

export default GlobalPresetsList;

